// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Button, Collapse, Grid, Group, Loader, Modal, Paper, Stack, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type {
  DocumentReference,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
} from '@medplum/fhirtypes';
import { QuestionnaireForm, useMedplum, useMedplumProfile } from '@medplum/react';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { type JSX, useState } from 'react';
import type { AssessmentState } from '../../../hooks/usePatientAssessments';
import {
  createVersionedQuestionnaireDocument,
  getQuestionnaireDocumentSource,
  QUESTIONNAIRE_DOCUMENT_TYPE_CODE,
  QUESTIONNAIRE_DOCUMENT_TYPE_SYSTEM,
} from '../../../utils/questionnaire-document';
import { withTenantScope } from '../../../utils/tenant';

dayjs.extend(relativeTime);

function formatLastCompleted(response: QuestionnaireResponse | undefined): string {
  if (!response) {
    return 'Never';
  }
  const value = response.authored ?? response.meta?.lastUpdated;
  if (!value) {
    return 'Never';
  }
  return dayjs(value).fromNow();
}

interface FillAssessmentModalProps {
  patientId: string;
  patient: Patient;
  assessment: AssessmentState | undefined;
  mode: 'review' | 'fill' | null;
  onClose: () => void;
  onSaved: () => void;
  onDocumentCreated: () => void;
}

function FillAssessmentModal({
  patientId,
  patient,
  assessment,
  mode,
  onClose,
  onSaved,
  onDocumentCreated,
}: FillAssessmentModalProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [saving, setSaving] = useState(false);

  const questionnaire = assessment?.questionnaire;
  const existing = mode === 'review' ? assessment?.latestResponse : undefined;

  const title = mode === 'review' ? 'Review Assessment' : 'Fill Out Assessment';
  const opened = Boolean(assessment && mode);

  const handleSubmit = async (response: QuestionnaireResponse): Promise<void> => {
    if (!questionnaire?.id) {
      showNotification({ color: 'red', message: 'Questionnaire not found' });
      return;
    }
    setSaving(true);
    try {
      const savedResponse = await medplum.createResource<QuestionnaireResponse>(
        withTenantScope({
          ...response,
          status: 'completed',
          subject: { reference: `Patient/${patientId}` },
          questionnaire: `Questionnaire/${questionnaire.id}`,
          authored: new Date().toISOString(),
        })
      );
      const previousDocs = await medplum.searchResources('DocumentReference', {
        subject: `Patient/${patientId}`,
        status: 'current',
        type: `${QUESTIONNAIRE_DOCUMENT_TYPE_SYSTEM}|${QUESTIONNAIRE_DOCUMENT_TYPE_CODE}`,
      });
      const previous = (previousDocs as DocumentReference[]).find(
        (doc) => getQuestionnaireDocumentSource(doc).questionnaireName === questionnaire.name
      );
      await createVersionedQuestionnaireDocument({
        medplum,
        patient,
        practitioner: profile,
        questionnaire,
        response: savedResponse,
        previous,
      });
      onSaved();
      onDocumentCreated();
      onClose();
      showNotification({ color: 'green', message: 'Assessment saved' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg" centered>
      {questionnaire ? (
        <Stack gap="md">
          <QuestionnaireForm
            questionnaire={questionnaire as Questionnaire}
            questionnaireResponse={existing}
            onSubmit={handleSubmit}
            submitButtonText={saving ? 'Saving…' : 'Save'}
          />
        </Stack>
      ) : (
        <Text c="dimmed">This questionnaire is not available in this environment.</Text>
      )}
    </Modal>
  );
}

export interface AssessmentsSectionProps {
  patientId: string;
  patient: Patient;
  assessments: AssessmentState[];
  loading: boolean;
  onRefresh: () => void;
  onDocumentCreated?: () => void;
}

export function AssessmentsSection({
  patientId,
  patient,
  assessments,
  loading,
  onRefresh,
  onDocumentCreated = () => undefined,
}: AssessmentsSectionProps): JSX.Element {
  const [collapsed, setCollapsed] = useState(false);
  const [target, setTarget] = useState<AssessmentState | undefined>();
  const [mode, setMode] = useState<'review' | 'fill' | null>(null);

  const openReview = (a: AssessmentState): void => {
    setTarget(a);
    setMode('review');
  };

  const openFill = (a: AssessmentState): void => {
    setTarget(a);
    setMode('fill');
  };

  const closeModal = (): void => {
    setTarget(undefined);
    setMode(null);
  };

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md">
        <Title order={2}>Assessments</Title>
        <Button
          variant="subtle"
          size="sm"
          rightSection={collapsed ? <IconChevronDown size={14} /> : <IconChevronUp size={14} />}
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? 'Open' : 'Close'}
        </Button>
      </Group>

      <Collapse in={!collapsed}>
        {loading ? (
          <Stack align="center" py="md">
            <Loader size="sm" />
          </Stack>
        ) : (
          <Grid>
            {assessments.map((assessment) => {
              const hasResponse = Boolean(assessment.latestResponse);
              const available = Boolean(assessment.questionnaire);
              return (
                <Grid.Col key={assessment.entry.key} span={{ base: 12, md: 6 }}>
                  <Paper withBorder radius="md" p="md" style={{ height: '100%' }}>
                    <Stack gap="xs">
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Text fw={600}>{assessment.entry.title}</Text>
                        <Badge variant="light" color="gray">
                          {assessment.entry.itemCount} {assessment.entry.itemLabel}
                        </Badge>
                      </Group>
                      <Text size="sm" c="dimmed">
                        {assessment.entry.description}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Last Completed: {formatLastCompleted(assessment.latestResponse)}
                      </Text>
                      <Group gap="xs" mt="xs">
                        {hasResponse ? (
                          <>
                            <Button
                              size="xs"
                              variant="light"
                              onClick={() => openReview(assessment)}
                              disabled={!available}
                            >
                              Review
                            </Button>
                            <Button
                              size="xs"
                              variant="default"
                              onClick={() => openFill(assessment)}
                              disabled={!available}
                            >
                              New Instance
                            </Button>
                          </>
                        ) : (
                          <Button size="xs" variant="light" onClick={() => openFill(assessment)} disabled={!available}>
                            Fill Out
                          </Button>
                        )}
                      </Group>
                    </Stack>
                  </Paper>
                </Grid.Col>
              );
            })}
          </Grid>
        )}
      </Collapse>

      <FillAssessmentModal
        patientId={patientId}
        patient={patient}
        assessment={target}
        mode={mode}
        onClose={closeModal}
        onSaved={onRefresh}
        onDocumentCreated={onDocumentCreated}
      />
    </Paper>
  );
}
